import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Leaf, ShieldCheck, FlaskConical, Sprout, RefreshCcw, BadgeCheck, Star, BookOpen } from "lucide-react";
import { SHOP_ALL_URL } from "@/data/products";
import { HERO_IMAGE as heroImage } from "@/lib/preloadImages";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HERO_HANDLE = "nad"; // the bottle featured in the hero

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.12 },
  }),
};

const CATALOG_PDF = `${process.env.REACT_APP_BACKEND_URL}/api/catalog.pdf`;

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // Show the bundled cutout instantly, then upgrade to the live (auto-updated, background-removed) Shopify image.
  const [heroSrc, setHeroSrc] = useState(heroImage);
  useEffect(() => {
    const live = `${BACKEND}/api/products/${HERO_HANDLE}/image.webp`;
    const pre = new Image();
    pre.onload = () => setHeroSrc(live);
    pre.src = live;
    return () => {
      pre.onload = null;
    };
  }, []);

  const lines = ["Clinical", "precision,", "natural"];

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden pb-16 pt-[calc(var(--ann-h,0px)+7rem)] lg:pt-[calc(var(--ann-h,0px)+9rem)]"
    >
      {/* floating organic accent */}
      <motion.div
        style={{ y: yBlob }}
        className="pointer-events-none absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-[#ff8a3d]/25 blur-3xl"
      />
      <motion.div
        style={{ y: yBlob }}
        className="pointer-events-none absolute top-1/2 -left-40 h-[380px] w-[380px] rounded-full bg-[#e85d04]/10 blur-3xl"
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* left copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#4a524c]">
              <Leaf size={14} className="text-[#e85d04]" />
              Third-party tested • Clinically dosed
            </span>
          </motion.div>

          <h1 className="font-serif-display font-light leading-[0.92] tracking-tight text-[#1a1f1b] text-[15vw] sm:text-[11vw] lg:text-[7.5rem]">
            {lines.map((t, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {t}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden">
              <motion.span
                custom={3}
                variants={line}
                initial="hidden"
                animate="visible"
                className="block italic text-[#e85d04]"
              >
                alchemy.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-md text-base sm:text-lg leading-relaxed text-[#4a524c]"
          >
            Premium vitamins, nootropics, adaptogens and longevity formulas —
            engineered at research doses, sourced from nature.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={SHOP_ALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-shop-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#e85d04] text-[#f4f1eb] px-8 py-4 text-sm hover:bg-[#b34700] transition-colors duration-300"
            >
              Shop the collection
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#products"
              data-testid="hero-explore-btn"
              className="inline-flex items-center rounded-full border border-[#1a1f1b]/30 px-8 py-4 text-sm text-[#1a1f1b] hover:bg-[#1a1f1b] hover:text-[#f4f1eb] transition-colors duration-300"
            >
              Explore formulas
            </a>
            <a
              href={CATALOG_PDF}
              download="Alpha-Omega-Vitality-Catalog.pdf"
              data-testid="hero-catalog-btn"
              className="inline-flex items-center gap-2 px-2 py-4 text-sm text-[#1a1f1b] underline decoration-[#e85d04] decoration-2 underline-offset-[6px] hover:text-[#b34700] transition-colors duration-300"
            >
              <BookOpen size={17} /> Download our Catalog
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            className="mt-14 flex items-center gap-8 border-t border-[#1a1f1b]/10 pt-6"
          >
            {[
              ["30+", "Precision formulas"],
              ["100%", "Lab verified"],
              ["4.9★", "Customer rating"],
            ].map(([k, v]) => (
              <div key={v}>
                <div className="font-serif-display text-3xl text-[#1a1f1b]">{k}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#4a524c] mt-1">{v}</div>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.55 }}
            data-testid="hero-trust-strip"
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3"
          >
            {[
              [ShieldCheck, "Third-Party Tested"],
              [FlaskConical, "Clinically Dosed"],
              [Sprout, "Non-GMO"],
              [RefreshCcw, "Subscribe & Save"],
            ].map(([Icon, label]) => (
              <span key={label} className="inline-flex items-center gap-2 text-xs text-[#4a524c]">
                <Icon size={15} className="text-[#c79a3e]" />
                {label}
              </span>
            ))}
            <span className="inline-flex items-center gap-2 text-xs text-[#4a524c]">
              <img src="/usa-flag.webp" alt="American flag" className="h-4 w-6 rounded-[2px] object-cover shadow-sm ring-1 ring-black/5" />
              Sourced &amp; Shipped in the USA
            </span>
          </motion.div>
        </div>

        {/* right image */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto max-w-md"
          >
            <div className="clip-arch overflow-hidden bg-gradient-to-b from-[#eae5dc] to-[#e0d8ca] border border-[#1a1f1b]/10 aspect-[3/4]">
              <motion.img
                style={{ y: yImg, scale: scaleImg }}
                src={heroSrc}
                alt="Alpha Omega Vitality NAD+ longevity supplement bottle"
                className="h-full w-full object-contain p-8 drop-shadow-[0_25px_35px_rgba(26,31,27,0.25)]"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width="675"
                height="1200"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="absolute -bottom-6 -left-6 bg-[#1d231e] text-[#f4f1eb] rounded-2xl px-5 py-4 shadow-xl ring-1 ring-[#c79a3e]/40"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#c79a3e]">★ Bestseller</div>
              <div className="font-serif-display text-xl">NAD+ Longevity</div>
            </motion.div>

            {/* floating verified chip */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
              transition={{
                opacity: { delay: 1.35, duration: 0.5 },
                scale: { delay: 1.35, duration: 0.5 },
                y: { delay: 1.9, duration: 4.5, repeat: Infinity, ease: "easeInOut" },
              }}
              style={{ willChange: "transform" }}
              data-testid="hero-chip-verified"
              className="absolute -top-4 -right-3 flex items-center gap-2 rounded-full border border-black/5 bg-[#fbf9f5] px-4 py-2.5 shadow-lg sm:-right-6"
            >
              <BadgeCheck size={18} className="text-[#2f9e44]" />
              <div className="leading-tight">
                <div className="text-xs font-semibold text-[#1a1f1b]">Lab Verified</div>
                <div className="text-[10px] text-[#4a524c]">Every batch</div>
              </div>
            </motion.div>

            {/* floating rating chip */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1, y: [0, 9, 0] }}
              transition={{
                opacity: { delay: 1.5, duration: 0.5 },
                scale: { delay: 1.5, duration: 0.5 },
                y: { delay: 2.1, duration: 5, repeat: Infinity, ease: "easeInOut" },
              }}
              style={{ willChange: "transform" }}
              data-testid="hero-chip-rating"
              className="absolute top-1/3 -right-4 flex items-center gap-2 rounded-2xl border border-black/5 bg-[#fbf9f5] px-4 py-3 shadow-lg sm:-right-10"
            >
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={12} className="fill-[#c79a3e] text-[#c79a3e]" />
                ))}
              </div>
              <span className="text-xs font-semibold text-[#1a1f1b]">4.9</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
